import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { BookingDTO } from "../../domain/contracts/bookings";
import { isMultiMemberPackageKind } from "../../domain/lib/package-kind";
import { PrivateLessonBookingLocationCell } from "./PrivateLessonBookingLocationCell";
import { PrivateLessonGroupPackageBadge } from "./PrivateLessonGroupPackageBadge";

type BookingRow = BookingDTO;

type BookingsTableProps = {
  data: BookingRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (booking: BookingRow) => void;
  onEdit: (booking: BookingRow) => void;
  onCancel: (id: string) => void;
};

export function BookingsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onCancel,
}: BookingsTableProps) {
  const columns: TanstackReactTable.ColumnDef<BookingRow>[] = [
    {
      header: "ນັກຮຽນ",
      accessorKey: "studentName",
      cell: ({ row }) => {
        const b = row.original;
        return (
          <div>
            <div className="flex flex-wrap items-center gap-2 font-semibold">
              {b.studentName}
              <PrivateLessonGroupPackageBadge
                packageKind={b.packageKind}
                groupSize={b.groupSize}
              />
            </div>
            <div className="text-muted-foreground text-xs">
              {b.studentMemberId}
              {isMultiMemberPackageKind(b.packageKind) &&
                b.memberAttendanceCount != null &&
                b.groupSize != null && (
                  <span className="ml-2">
                    · ກວດຊື່ {b.memberAttendanceCount}/{b.groupSize}
                  </span>
                )}
            </div>
          </div>
        );
      },
    },
    createSortableColumn("date", "ວັນທີ", {
      cell: ({ row }) => formatDate(row.original.date),
    }),
    {
      header: "ເວລາ",
      accessorKey: "startTime",
      cell: ({ row }) => (
        <div>
          {formatTime(row.original.startTime)} -{" "}
          {formatTime(row.original.endTime)}
        </div>
      ),
    },
    {
      header: "ລາຍການ",
      accessorKey: "durationMinutes",
      cell: ({ row }) => `${row.original.durationMinutes} ນາທີ`,
    },
    {
      header: "ສະຖານທີ່ / ຫ້ອງ",
      accessorKey: "roomName",
      cell: ({ row }) => {
        const b = row.original;
        return (
          <PrivateLessonBookingLocationCell
            locationKind={b.locationKind}
            offSiteLabel={b.offSiteLabel}
            offSiteDistance={b.offSiteDistance}
            roomName={b.roomName}
          />
        );
      },
    },
    {
      header: "ຄູຜູ້ສອນ",
      accessorKey: "instructorNames",
      cell: ({ row }) => (row.original.instructorNames ?? []).join(", ") || "-",
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "scheduled"
            ? "default"
            : status === "completed"
              ? "secondary"
              : "outline";
        const label =
          status === "scheduled"
            ? "ຈອງແລ້ວ"
            : status === "completed"
              ? "ສຳເລັດ"
              : "ຍົກເລີກ";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: BookingRow } }) => {
        const booking = row.original;
        const canEdit = booking.status === "scheduled";
        const canCancel = booking.status === "scheduled";
        const actions = [];

        actions.push({
          label: "ເບິ່ງລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(booking),
        });

        if (canEdit) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(booking),
          });
        }

        if (canCancel) {
          actions.push({
            label: "ຍົກເລີກ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ຍົກເລີກການຈອງ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກການຈອງນີ້?",
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onCancel(booking.id);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<BookingRow, unknown>
      noDataMessage="ບໍ່ພົບການຈອງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
